// =============================================================
// i18n core — framework-free dictionary + DOM binding.
//   - t(key, params): lookup current locale, interpolate {param}.
//   - initI18n(): read stored locale, paint static DOM.
//   - setLocale(): switch locale, persist, repaint, notify subscribers.
//   - applyDom(): walk [data-i18n] / [data-i18n-html] / [data-i18n-attr].
// =============================================================

import { ko } from './locales/ko';
import { en } from './locales/en';
import { ja } from './locales/ja';
import { zh } from './locales/zh';

export type Locale = 'ko' | 'en' | 'ja' | 'zh';
export type Dict = Record<string, string>;

const DICTS: Record<Locale, Dict> = { ko, en, ja, zh };
const SUPPORTED: Locale[] = ['ko', 'en', 'ja', 'zh'];
const DEFAULT_LOCALE: Locale = 'en';
const STORAGE_KEY = 'digmol.locale';

let current: Locale = DEFAULT_LOCALE;
const listeners = new Set<() => void>();

export function getLocale(): Locale {
  return current;
}

/** Translate `key`, falling back to the default locale then the raw key.
 *  `{name}` placeholders are replaced from `params`. */
export function t(key: string, params?: Record<string, string | number>): string {
  let s = DICTS[current][key] ?? DICTS[DEFAULT_LOCALE][key] ?? key;
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      s = s.split(`{${k}}`).join(String(v));
    }
  }
  return s;
}

function readStored(): Locale {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v && (SUPPORTED as string[]).includes(v)) return v as Locale;
  } catch {
    /* localStorage unavailable */
  }
  return DEFAULT_LOCALE;
}

/** Repaint every element tagged with a data-i18n* attribute. */
export function applyDom(root: ParentNode = document): void {
  root.querySelectorAll<HTMLElement>('[data-i18n]').forEach((el) => {
    el.textContent = t(el.dataset.i18n!);
  });
  root.querySelectorAll<HTMLElement>('[data-i18n-html]').forEach((el) => {
    el.innerHTML = t(el.dataset.i18nHtml!);
  });
  // data-i18n-attr="placeholder:swap.bnbPlaceholder;aria-label:modal.close"
  root.querySelectorAll<HTMLElement>('[data-i18n-attr]').forEach((el) => {
    for (const pair of el.dataset.i18nAttr!.split(';')) {
      const [attr, key] = pair.split(':');
      if (attr && key) el.setAttribute(attr.trim(), t(key.trim()));
    }
  });
}

/** Switch locale: persist, update <html lang>, repaint, notify listeners. */
export function setLocale(locale: Locale): void {
  current = locale;
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    /* localStorage unavailable */
  }
  document.documentElement.lang = locale;
  applyDom();
  listeners.forEach((fn) => fn());
}

/** Register a callback fired after every locale switch.
 *  Use this to repaint JS-rendered (non-static) content. */
export function onLocaleChange(fn: () => void): void {
  listeners.add(fn);
}

/** Boot: resolve stored locale and paint the static DOM once. */
export function initI18n(): void {
  current = readStored();
  document.documentElement.lang = current;
  applyDom();
}
